/**
 * Plans Zustand Store
 *
 * Manages all state for the Plans section including:
 * - Plan list with filters and pagination
 * - Active plan details
 * - Plan CRUD operations
 * - Subscriber list for plans
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  PlanWithStats,
  SuperAdminPlan,
  PlanListFilters,
  PaginationMeta,
  CreatePlanDto,
  UpdatePlanDto,
  AccountListItem,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface PlansError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface PlansState {
  // Plans List
  plans: PlanWithStats[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: PlansError | null;

  // Filters
  filters: PlanListFilters;

  // Active Plan (SuperAdminPlan when loaded individually, PlanWithStats when from list)
  activePlanId: string | null;
  activePlan: PlanWithStats | SuperAdminPlan | null;
  isLoadingPlan: boolean;
  planError: PlansError | null;

  // Plan Subscribers
  subscribers: AccountListItem[];
  subscribersPagination: PaginationMeta | null;
  isLoadingSubscribers: boolean;

  // Action States
  isPerformingAction: boolean;
  actionError: PlansError | null;

  // Actions - List
  loadPlans: (page?: number, includeInactive?: boolean) => Promise<void>;
  refreshPlans: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof PlanListFilters>(key: K, value: PlanListFilters[K]) => void;
  clearFilters: () => void;

  // Actions - Active Plan
  loadPlan: (id: string) => Promise<void>;
  selectPlan: (id: string) => void;
  clearActivePlan: () => void;

  // Actions - Subscribers
  loadSubscribers: (planId: string, page?: number) => Promise<void>;

  // Actions - CRUD
  createPlan: (dto: CreatePlanDto) => Promise<string | null>;
  updatePlan: (id: string, dto: UpdatePlanDto) => Promise<boolean>;
  deletePlan: (id: string) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Plans List
  plans: [] as PlanWithStats[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as PlansError | null,

  // Filters
  filters: {} as PlanListFilters,

  // Active Plan
  activePlanId: null as string | null,
  activePlan: null as (PlanWithStats | SuperAdminPlan | null),
  isLoadingPlan: false,
  planError: null as PlansError | null,

  // Plan Subscribers
  subscribers: [] as AccountListItem[],
  subscribersPagination: null as PaginationMeta | null,
  isLoadingSubscribers: false,

  // Action States
  isPerformingAction: false,
  actionError: null as PlansError | null,
});

// ============================================
// Store Implementation
// ============================================

export const usePlansStore = create<PlansState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadPlans: async (page = 1, includeInactive = false) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (includeInactive) params.set('includeInactive', 'true');

      const response = await api.get<PlanWithStats[]>(
        `${API_ENDPOINTS.SUPER_ADMIN.PLANS.LIST}?${params.toString()}`
      );

      set({
        plans: response,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_PLANS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load plans',
        },
      });
    }
  },

  refreshPlans: async () => {
    const { pagination } = get();
    await get().loadPlans(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadPlans(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadPlans(1);
  },

  // ============================================
  // Active Plan Actions
  // ============================================

  loadPlan: async (id: string) => {
    set({ activePlanId: id, isLoadingPlan: true, planError: null });
    try {
      const api = getApiClient();
      const response = await api.get<SuperAdminPlan>(API_ENDPOINTS.SUPER_ADMIN.PLANS.PLAN(id));

      set({ activePlan: response, isLoadingPlan: false });
    } catch (error) {
      set({
        isLoadingPlan: false,
        planError: {
          code: 'LOAD_PLAN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load plan',
        },
      });
    }
  },

  selectPlan: (id: string) => {
    const { plans } = get();
    const plan = plans.find((p) => p.id === id);
    if (plan) {
      set({ activePlanId: id, activePlan: plan });
    } else {
      get().loadPlan(id);
    }
  },

  clearActivePlan: () => {
    set({
      activePlanId: null,
      activePlan: null,
      subscribers: [],
      subscribersPagination: null,
      planError: null,
    });
  },

  // ============================================
  // Subscriber Actions
  // ============================================

  loadSubscribers: async (planId: string, page = 1) => {
    set({ isLoadingSubscribers: true });
    try {
      const api = getApiClient();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      const response = await api.get<{ subscribers: AccountListItem[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.PLANS.SUBSCRIBERS(planId)}?${params.toString()}`
      );

      set({
        subscribers: response.subscribers,
        subscribersPagination: response.pagination,
        isLoadingSubscribers: false,
      });
    } catch {
      set({ isLoadingSubscribers: false });
    }
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createPlan: async (dto: CreatePlanDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<SuperAdminPlan>(API_ENDPOINTS.SUPER_ADMIN.PLANS.CREATE, dto);

      // Refresh the list
      await get().refreshPlans();
      set({ isPerformingAction: false });
      return response.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_PLAN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create plan',
        },
      });
      return null;
    }
  },

  updatePlan: async (id: string, dto: UpdatePlanDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<SuperAdminPlan>(API_ENDPOINTS.SUPER_ADMIN.PLANS.UPDATE(id), dto);

      // Update local state
      set((state) => ({
        plans: state.plans.map((p) =>
          p.id === id ? { ...p, ...response } : p
        ),
        activePlan:
          state.activePlan?.id === id
            ? { ...state.activePlan, ...response }
            : state.activePlan,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_PLAN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update plan',
        },
      });
      return false;
    }
  },

  deletePlan: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.PLANS.DELETE(id));

      // Remove from local state
      set((state) => ({
        plans: state.plans.filter((p) => p.id !== id),
        activePlan: state.activePlan?.id === id ? null : state.activePlan,
        activePlanId: state.activePlanId === id ? null : state.activePlanId,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_PLAN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete plan',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, planError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get active plans count */
export const useActivePlansCount = () => {
  return usePlansStore((state) =>
    state.plans.filter((p) => p.isActive).length
  );
};

/** Get total MRR from all plans */
export const useTotalMrr = () => {
  return usePlansStore((state) =>
    state.plans.reduce((sum, p) => sum + p.mrrContribution, 0)
  );
};

/** Check if any loading state is active */
export const usePlansLoading = () => {
  const isLoadingList = usePlansStore((state) => state.isLoadingList);
  const isLoadingPlan = usePlansStore((state) => state.isLoadingPlan);
  const isPerformingAction = usePlansStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingPlan || isPerformingAction;
};

/** Get plan by ID */
export const usePlanById = (id: string) => {
  return usePlansStore((state) =>
    state.plans.find((p) => p.id === id)
  );
};
